import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Loader2, Plus, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { adminInputClass } from '@/components/admin/AdminTableStyles';
import { filterAccountGroups } from '@/lib/accounting';
import { cn } from '@/lib/utils';

const PANEL_HEIGHT = 340;

/**
 * Choosing a category, which is not choosing from a dropdown.
 *
 * A flat `<select>` of forty accounts is unusable and, worse, silently
 * ambiguous — "Rent" as income and "Rent" as an expense look identical in a
 * list that has thrown its groups away. So this keeps three things a plain
 * select cannot:
 *
 *   * the **grouping**, which survives searching: a group with a match stays,
 *     an empty one drops out, and the headings never disappear;
 *   * the **help text** each account carries, shown under its name, because
 *     the difference between "Refunds Issued" and "Other Expenses" lives in
 *     that sentence and nowhere else;
 *   * a way to **add the account you actually needed** without losing the form
 *     you were half way through filling in.
 *
 * Two sizes and two mountings.
 *
 * `variant="field"` is the full-height control in the edit dialog;
 * `variant="cell"` is the same box at table scale — shorter, smaller type, no
 * account code or help text beneath it. Both are *boxes* with a chevron rather
 * than underlined text, because a bordered control is the thing people already
 * know they can click.
 *
 * The mountings differ because the two places clip differently:
 *
 *   `portal={false}` (the default, used inside the edit dialog) positions the
 *   panel absolutely inside the component. It stays in the React tree, so the
 *   dialog's focus trap and outside-click detection treat it as inside — which
 *   a body-level portal would not, and the dialog would close under you.
 *
 *   `portal` (used in the transactions table) renders the panel into
 *   `document.body` at fixed coordinates taken from the trigger. The table
 *   scrolls horizontally, and `overflow-x: auto` computes `overflow-y` to
 *   `auto` as well, so an absolutely positioned panel would be clipped to the
 *   row. There is no dialog around the table, so escaping the tree costs
 *   nothing.
 */
export default function CategoryPicker({
  value,
  onChange,
  groups = [],
  accountsById = {},
  accountTypes = [],
  canCreate = false,
  onCreate,
  disabled = false,
  excludeId = '',
  placeholder = 'Choose a category…',
  id,
  invalid = false,
  variant = 'field',
  portal = false,
  emphasis = 'normal',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [rect, setRect] = useState(null);
  const wrapper = useRef(null);
  const panel = useRef(null);
  const trigger = useRef(null);
  const searchBox = useRef(null);

  const selected = accountsById[value] || null;

  // The account the money is sitting in cannot also be what it is filed under
  // — that is what the server refuses — so it is not offered.
  const available = useMemo(
    () =>
      (groups || [])
        .map((group) => ({
          ...group,
          accounts: (group.accounts || []).filter((account) => account.id !== excludeId),
        }))
        .filter((group) => group.accounts.length > 0),
    [groups, excludeId]
  );

  const visible = useMemo(() => filterAccountGroups(available, query), [available, query]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      const insideTrigger = wrapper.current?.contains(event.target);
      const insidePanel = panel.current?.contains(event.target);
      if (!insideTrigger && !insidePanel) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  // Track where the trigger is, for the portalled mounting. Recomputed on
  // scroll rather than closed on it, so the panel follows the row instead of
  // vanishing the moment the table nudges sideways.
  useLayoutEffect(() => {
    if (!open || !portal) return undefined;
    const update = () => setRect(trigger.current?.getBoundingClientRect() || null);
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, portal]);

  useEffect(() => {
    if (open) searchBox.current?.focus();
    else {
      setQuery('');
      setCreating(null);
      setError('');
    }
  }, [open]);

  const choose = (account) => {
    onChange?.(account.id);
    setOpen(false);
  };

  const startCreating = () => {
    setError('');
    setCreating({
      // Whatever was typed into the search is almost always the name of the
      // thing that was not there, so it becomes the name rather than being
      // thrown away.
      name: query.trim(),
      account_type: 'expense',
      code: '',
      description: '',
    });
  };

  const submitCreate = async () => {
    if (!creating?.name?.trim()) {
      setError('The new category needs a name.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const created = await onCreate?.(creating);
      if (created?.id) {
        onChange?.(created.id);
        setOpen(false);
      }
    } catch (err) {
      setError(err?.message || 'That category could not be added.');
    } finally {
      setSaving(false);
    }
  };

  const panelBody = (
    <div
      ref={panel}
      className="overflow-hidden rounded-lg border border-[#062B1A]/12 bg-white shadow-xl"
    >
      <div className="flex items-center gap-2 border-b border-[#062B1A]/8 px-3 py-2">
        <Search className="size-3.5 shrink-0 text-[#062B1A]/35" />
        <input
          ref={searchBox}
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search categories"
          aria-label="Search categories"
          className="w-full bg-transparent text-sm outline-none placeholder:text-[#062B1A]/35"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery('')}
            aria-label="Clear the category search"
            className="text-[#062B1A]/35 hover:text-[#062B1A]"
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>

      {creating ? (
        <div className="space-y-2.5 p-3">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/45">
            New category
          </p>
          <div>
            <Label htmlFor="new-category-name" className="text-xs">
              Name
            </Label>
            <Input
              id="new-category-name"
              value={creating.name}
              onChange={(event) => setCreating({ ...creating, name: event.target.value })}
              className={cn('mt-1 h-9', adminInputClass)}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="new-category-type" className="text-xs">
                Account type
              </Label>
              <select
                id="new-category-type"
                value={creating.account_type}
                onChange={(event) => setCreating({ ...creating, account_type: event.target.value })}
                className={cn(
                  'mt-1 h-9 w-full rounded-md border bg-white px-2 text-sm',
                  adminInputClass
                )}
              >
                {(accountTypes.length
                  ? accountTypes
                  : [
                      { value: 'expense', label: 'Expense accounts' },
                      { value: 'income', label: 'Income accounts' },
                    ]
                ).map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="new-category-code" className="text-xs">
                Account ID <span className="text-[#062B1A]/35">(optional)</span>
              </Label>
              <Input
                id="new-category-code"
                value={creating.code}
                onChange={(event) => setCreating({ ...creating, code: event.target.value })}
                className={cn('mt-1 h-9', adminInputClass)}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="new-category-description" className="text-xs">
              What it is for <span className="text-[#062B1A]/35">(optional)</span>
            </Label>
            <Input
              id="new-category-description"
              value={creating.description}
              onChange={(event) => setCreating({ ...creating, description: event.target.value })}
              placeholder="Shown under the name, so the next person picks the right one"
              className={cn('mt-1 h-9', adminInputClass)}
            />
          </div>
          {error && <p className="text-xs text-red-700">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" size="sm" onClick={() => setCreating(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={saving}
              onClick={submitCreate}
              className="bg-[#062B1A] text-white hover:bg-[#041B10]"
            >
              {saving && <Loader2 className="mr-1.5 size-3.5 animate-spin" />}
              Add category
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="max-h-72 overflow-y-auto py-1">
            {visible.length === 0 ? (
              <p className="px-3 py-6 text-center text-xs text-[#062B1A]/45">
                Nothing matches “{query}”.
              </p>
            ) : (
              visible.map((group) => (
                <div key={group.key} className="pb-1">
                  <p className="sticky top-0 bg-white px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#062B1A]/40">
                    {group.label}
                  </p>
                  {group.accounts.map((account) => (
                    <button
                      key={account.id}
                      type="button"
                      onClick={() => choose(account)}
                      className={cn(
                        'flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-[#F7F8FA]',
                        account.id === value && 'bg-[#062B1A]/5'
                      )}
                    >
                      <Check
                        className={cn(
                          'mt-0.5 size-3.5 shrink-0',
                          account.id === value ? 'text-[#062B1A]' : 'text-transparent'
                        )}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-baseline justify-between gap-2">
                          <span className="truncate text-sm text-[#062B1A]">{account.name}</span>
                          {account.code && (
                            <span className="shrink-0 font-mono text-[11px] text-[#062B1A]/35">
                              {account.code}
                            </span>
                          )}
                        </span>
                        {account.description && (
                          <span className="mt-0.5 block text-[11px] leading-snug text-[#062B1A]/45">
                            {account.description}
                          </span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
          {canCreate && (
            <button
              type="button"
              onClick={startCreating}
              className="flex w-full items-center gap-2 border-t border-[#062B1A]/8 px-3 py-2.5 text-left text-sm font-medium text-[#062B1A] hover:bg-[#F7F8FA]"
            >
              <Plus className="size-3.5" />
              Add a new category
              {query.trim() && (
                <span className="truncate text-[#062B1A]/45">“{query.trim()}”</span>
              )}
            </button>
          )}
        </>
      )}
    </div>
  );

  // `cell` is the same control at table scale: a shorter box, smaller type, no
  // account code and no help text under it. It stays a *box* rather than a
  // text link, because a bordered control with a chevron is the thing people
  // already know they can click — an underline is a guess.
  const cell = variant === 'cell';

  return (
    <div className={cn(!portal && 'relative')} ref={wrapper}>
      <button
        type="button"
        id={id}
        ref={trigger}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-md border bg-white text-left',
          'border-[rgba(6,43,26,0.16)] focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50',
          cell
            ? 'h-8 min-w-[9rem] max-w-[15rem] px-2 text-xs hover:border-[#062B1A]/35'
            : 'h-10 px-3 text-sm',
          open && 'border-[#062B1A]/45',
          invalid && 'border-red-400',
          disabled && 'cursor-not-allowed bg-[#F7F8FA] opacity-70'
        )}
      >
        <span
          className={cn(
            'min-w-0 truncate',
            !selected && 'text-[#062B1A]/40',
            selected && emphasis === 'muted' ? 'italic text-[#062B1A]/55' : ''
          )}
        >
          {selected ? selected.name : placeholder}
        </span>
        <span className="flex shrink-0 items-center gap-1">
          {!cell && selected?.code && (
            <span className="font-mono text-[11px] text-[#062B1A]/35">{selected.code}</span>
          )}
          <ChevronDown className={cn('text-[#062B1A]/40', cell ? 'size-3.5' : 'size-4')} />
        </span>
      </button>

      {!cell && selected?.description && !open && (
        <p className="mt-1 line-clamp-2 text-[11px] leading-snug text-[#062B1A]/45">
          {selected.description}
        </p>
      )}

      {open && !portal && (
        <div className="absolute left-0 right-0 z-50 mt-1 min-w-[18rem]">{panelBody}</div>
      )}

      {open && portal && rect
        && createPortal(
          <div
            className="fixed z-[60]"
            style={{
              left: Math.max(
                8,
                Math.min(rect.left, (window.innerWidth || 0) - 340)
              ),
              width: Math.max(rect.width, 320),
              // Flip above the trigger when there is not room below it, so a
              // row near the bottom of the page does not open off-screen.
              ...(rect.bottom + PANEL_HEIGHT > (window.innerHeight || 0)
                ? { bottom: (window.innerHeight || 0) - rect.top + 4 }
                : { top: rect.bottom + 4 }),
            }}
          >
            {panelBody}
          </div>,
          document.body
        )}
    </div>
  );
}
